import { useCallback, useEffect, useState } from "react";
import { naytaPvm } from "./pvm";
import { nayttonimi, selite } from "./nayttonimi";
import {
  api, ApiError,
  type Patevyystyyppi, type PatevyysRivi,
} from "./api";
import Liitteet from "./Liitteet";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   Henkilön pätevyydet — sama data kuin Pätevyydet-näkymässä,
   mutta henkilön alla. Osaamiset ja pätevyydet ovat eri asioita:
   osaaminen on luokka (mihin työhön), pätevyys on todistettu ja
   vanheneva oikeus. Kirjaus ja todistukset onnistuvat täältä.

   Tilamerkki johdetaan todistuksesta eikä lipusta: liite ON todiste.
   ═══════════════════════════════════════════════════════════ */

const tilaLuokka = (tila: string) =>
  tila === "vanhentunut" ? "vanhentunut"
  : tila === "kriittinen" ? "kriittinen"
  : tila === "varoitus" ? "varoitus"
  : "ok";

// Voimassaolon ehdotus: myöntöpäivä + tyypin voimassaolo kuukausina.
function laskeVoimassa(myonnetty: string, kk: number | null): string {
  if (!myonnetty || kk == null) return "";
  const d = new Date(myonnetty);
  if (Number.isNaN(d.getTime())) return "";
  d.setMonth(d.getMonth() + kk);
  return d.toISOString().slice(0, 10);
}

export default function HenkilonPatevyydet({
  henkiloId, nimi, tyypit, saaMuokata,
}: {
  henkiloId: number;
  nimi: string;
  tyypit: Patevyystyyppi[];
  saaMuokata: boolean;
}) {
  const [rivit, setRivit] = useState<PatevyysRivi[] | null>(null);
  const [avattu, setAvattu] = useState<number | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);

  // Kirjauslomake
  const [tyyppiId, setTyyppiId] = useState<number | "">("");
  const [varmennettu, setVarmennettu] = useState(false);
  const [myonnetty, setMyonnetty] = useState("");
  const [voimassa, setVoimassa] = useState("");
  const [voimassaKasin, setVoimassaKasin] = useState(false);
  const [tallennetaan, setTallennetaan] = useState(false);

  const lataa = useCallback(async () => {
    try { setRivit(await api.patevyydet(henkiloId)); setVirhe(null); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("nippu.haku_virhe")); setRivit([]); }
  }, [henkiloId]);
  useEffect(() => { void lataa(); }, [lataa]);

  // Ehdota voimassaoloa myöntöpäivästä ja tyypistä, ellei annettu käsin.
  const valittuTyyppi = tyypit.find((t) => t.id === tyyppiId);
  useEffect(() => {
    if (!voimassaKasin) setVoimassa(laskeVoimassa(myonnetty, valittuTyyppi?.voimassa_kk ?? null));
  }, [myonnetty, valittuTyyppi, voimassaKasin]);

  async function kirjaa() {
    if (tyyppiId === "") { setVirhe(t("hpa.valitse_tyyppi")); return; }
    setTallennetaan(true); setVirhe(null);
    try {
      await api.kirjaaPatevyys({
        henkilo_id: henkiloId, patevyystyyppi_id: tyyppiId, varmennettu,
        ...(myonnetty && { myonnetty }),
        ...(voimassa && { voimassa_asti: voimassa }),
      });
      setTyyppiId(""); setVarmennettu(false); setMyonnetty("");
      setVoimassa(""); setVoimassaKasin(false);
      await lataa();
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("hl.kirjaus_ei_onnistunut")); }
    finally { setTallennetaan(false); }
  }

  async function poista(id: number) {
    try { await api.poistaPatevyys(id); await lataa(); }
    catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("liite.poisto_virhe")); }
  }

  return (
    <div className="henkilopatevyys">
      <h3>{t("hpa.otsikko")}</h3>

      {rivit === null && <p className="dim">{t("ltar.ladataan")}</p>}
      {rivit && rivit.length === 0 && <p className="dim">{t("omat.ei_patevyyksia")}</p>}

      {rivit && rivit.length > 0 && (
        <ul className="henkilopatevyys__lista">
          {rivit.map((r) => (
            <li key={r.id} className="henkilopatevyys__rivi">
              <div className="henkilopatevyys__paa">
                {r.todistuksia > 0
                  ? <span className="merkki--todistus" title={t("pat.todistuksia_liitteena", { n: r.todistuksia })}>✓</span>
                  : r.varmennettu
                    ? <span className="merkki--nahty" title={t("pat.nahty_ei_liitteena")}>◑</span>
                    : <span className="merkki--epavarma" title={t("pat.ei_varmennettu_title")}>?</span>}
                <span className="henkilopatevyys__nimi">
                  {nayttonimi("patevyystyyppi_nimi", r.patevyys)}{r.taso && <span className="dim"> · {r.taso}</span>}
                </span>
                <span className={"tilamerkki tilamerkki--" + tilaLuokka(r.tila)} title={selite("patevyys_tila", r.tila)}>{nayttonimi("patevyys_tila", r.tila)}</span>
                <span className="dim mono henkilopatevyys__pvm">
                  {naytaPvm(r.voimassa_asti) ?? t("omat.ei_vanhene")}
                </span>
                <button type="button" className="nappi nappi--kevyt"
                        onClick={() => setAvattu(avattu === r.id ? null : r.id)}>
                  {avattu === r.id ? t("hl.sulje") : t("pat.todistus")}
                </button>
                {saaMuokata && (
                  <button type="button" className="henkilopatevyys__poista" title={t("hpa.poista")}
                          onClick={() => void poista(r.id)}>×</button>
                )}
              </div>
              {avattu === r.id && (
                <Liitteet
                  kohdeTaulu="patevyys" kohdeId={r.id}
                  saaMuokata={saaMuokata} otsikko={t("pat.todistus")}
                  voiMerkitaArkaluonteiseksi muuttui={lataa}
                />
              )}
            </li>
          ))}
        </ul>
      )}

      {saaMuokata && (
        <div className="henkilopatevyys__kirjaus">
          <select value={tyyppiId}
                  onChange={(e) => setTyyppiId(e.target.value ? Number(e.target.value) : "")}>
            <option value="">{t("hpa.lisaa")}</option>
            {tyypit.map((t) => <option key={t.id} value={t.id}>{t.nimi}</option>)}
          </select>
          <label className="henkilopatevyys__kentta">
            {t("pat.myonnetty")}
            <input type="date" value={myonnetty} onChange={(e) => setMyonnetty(e.target.value)} />
          </label>
          <label className="henkilopatevyys__kentta">
            {t("pat.voimassa_asti")}
            <input type="date" value={voimassa}
                   onChange={(e) => { setVoimassa(e.target.value); setVoimassaKasin(true); }} />
          </label>
          <label className="henkilopatevyys__ruksi">
            <input type="checkbox" checked={varmennettu}
                   onChange={(e) => setVarmennettu(e.target.checked)} />
            {t("hpa.todistus_nahty")}
          </label>
          <button type="button" className="nappi nappi--paa" disabled={tallennetaan || tyyppiId === ""}
                  onClick={() => void kirjaa()}>
            {tallennetaan ? t("hl.kirjataan") : t("hpa.kirjaa")}
          </button>
        </div>
      )}

      {virhe && <p className="huom" role="alert">{virhe}</p>}
      <p className="dim henkilopatevyys__vihje">
        {t("hpa.vihje", { nimi })}
      </p>
    </div>
  );
}
